const QUALITY_OPTIONS = ['Excellent', 'High', 'Medium', 'Good'];
const DURABILITY_OPTIONS = ['Excellent', 'Great', 'Good', 'Fair'];
const VALUE_OPTIONS = ['Excellent', 'Great', 'Good', 'Fair'];
const FEATURE_NOTES = ['Comprehensive feature set', 'Balanced performance', 'Lightweight design', 'Premium materials'];

const sanitizeNumber = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const numeric = parseFloat(value.replace(/[^0-9.,-]/g, '').replace(',', '.'));
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const getPrimaryImage = product => {
  if (!product) return '';
  if (product.image) return product.image;
  if (product.imageURL) return product.imageURL;
  if (Array.isArray(product.images) && product.images.length) return product.images[0];
  if (product.item?.mainImage) return product.item.mainImage;
  if (Array.isArray(product.thumbnailURLs) && product.thumbnailURLs.length) return product.thumbnailURLs[0];
  return '';
};

const pickRandom = (array, indexOffset = 0) => {
  if (!Array.isArray(array) || !array.length) return '';
  const index = Math.abs(Math.floor(Math.random() * array.length) + indexOffset) % array.length;
  return array[index];
};

const getDisplayPrice = (product, showPriceFn) => {
  if (typeof showPriceFn === 'function') {
    const value = showPriceFn(product, 'price', 'range');
    if (value && typeof value === 'string') return value;
  }

  const priceNumber = product?.price ?? product?.originalPrice;
  const currency = product?.currency || product?.priceRange?.currency || '$';

  if (priceNumber) {
    const normalized = sanitizeNumber(priceNumber);
    if (normalized) {
      return `${currency} ${normalized.toFixed(2)}`;
    }
  }

  return '';
};

export const transformToComparisonProduct = (product, options = {}) => {
  if (!product) return null;

  const priceNumber = sanitizeNumber(product.price ?? product.originalPrice ?? product.priceRange?.min);

  return {
    id: product.id,
    name: product.name || options.fallbackName || '',
    image: getPrimaryImage(product),
    price: priceNumber,
    currency: product.currency || product.priceRange?.currency || product.currencyRaw || '',
    displayPrice: getDisplayPrice(product, options.showPriceFn),
    isCurrent: !!options.isCurrent,
    attributes: product.attributes || {},
  };
};

const generateRating = index => {
  const base = 3.6 + Math.random() * 1.2; // Between 3.6 - 4.8
  const variance = (Math.random() - 0.5) * 0.3 + index * 0.05;
  return Number(Math.min(4.9, Math.max(3.5, base + variance)).toFixed(1));
};

const enrichProductAttributes = (product, index) => {
  const rating = generateRating(index);
  const quality = pickRandom(QUALITY_OPTIONS, index);
  const durability = pickRandom(DURABILITY_OPTIONS, QUALITY_OPTIONS.indexOf(quality));
  const value = pickRandom(VALUE_OPTIONS, index);
  const features = pickRandom(FEATURE_NOTES, index);

  return {
    ...product,
    attributes: {
      rating,
      quality,
      durability,
      value,
      features,
    },
  };
};

const computeBestValue = likiees => {
  if (!likiees || !likiees.length) return null;

  return [...likiees].reduce((best, item) => {
    const rating = item.attributes?.rating || 0;
    const price = item.price || 0;
    const score = rating * 20 - price / 25;

    if (!best) return { item, score };
    if (score > best.score) return { item, score };
    if (score === best.score && (item.price || Infinity) < (best.item.price || Infinity)) {
      return { item, score };
    }
    return best;
  }, null)?.item;
};

export const generateMockComparison = (likiees, context, t) => {
  const comparisonId = `comp_${Date.now()}`;
  const enrichedProducts = likiees.map((product, index) => enrichProductAttributes(product, index));
  const bestValue = computeBestValue(enrichedProducts);

  const verdict = bestValue
    ? t('ai_compare.best_value_summary', {
        product: bestValue.name,
        defaultValue: `${bestValue.name} offers the best quality-to-price ratio.`,
      })
    : '';

  return {
    id: comparisonId,
    products: enrichedProducts,
    context: context || '',
    verdict,
    createdAt: new Date().toISOString(),
    isBookmarked: false,
  };
};
